import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '@lingui/core/macro';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
} from '@mui/material';
import { useProject } from '../contexts/ProjectContext';
import NavBar from '../components/NavBar';
import type { Block } from '../classes/Block';
import type { CardGroup } from '../utils/cardGroups';
import {
  AutoFitTargetResult,
  CardTimingValues,
  CardTimelineItem,
  buildCardTimeline,
  formatFramesToClock,
  parseClockToFrames,
} from '../utils/cardTiming';
import './CardTiming.css';

interface UpdateCardTimingOptions {
  persist?: boolean;
}

export interface ApplyAutoFitResult extends AutoFitTargetResult {
  targetFrames: number;
  preservedFrames: number;
}

interface CardTimingProps {
  videoMode: 'cards' | 'roller';
  fps: number;
  blocks: Block[];
  cardGroups: CardGroup[];
  onUpdateCardGroupTiming: (
    cardGroupId: string,
    timingPatch: Partial<CardTimingValues>,
    options?: UpdateCardTimingOptions
  ) => Promise<void>;
  onApplyTimingToCards: (
    cardGroupIds: string[],
    timingPatch: Partial<CardTimingValues>
  ) => Promise<void>;
  onApplyAutoFitToCards: (
    targetFrames: number,
    fadeInFrames: number,
    fadeOutFrames: number,
    slugFrames: number,
    cardGroupIds: string[]
  ) => Promise<ApplyAutoFitResult>;
  onToggleCardGroupLock: (cardGroupId: string, isLocked: boolean) => Promise<void>;
}

interface TimingFieldConfig {
  field: keyof CardTimingValues;
  label: string;
}

const parseFrameInput = (value: string): number => {
  const parsed: number = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.floor(parsed));
};

const formatSignedClock = (frames: number, fps: number): string => {
  const absFrames: number = Math.abs(frames);
  const sign: string = frames < 0 ? '-' : frames > 0 ? '+' : '';
  return `${sign}${formatFramesToClock(absFrames, fps)}`;
};

interface RowInputProps {
  id: string;
  label: string;
  value: string | number;
  unit: string;
  type?: 'text' | 'number';
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}

const RowInput: React.FC<RowInputProps> = ({
  id,
  label,
  value,
  unit,
  type = 'number',
  placeholder,
  disabled = false,
  onChange,
}) => (
  <div className="card-row-input">
    <label htmlFor={id}>{label}</label>
    <div className="card-target-input-wrapper">
      <input
        id={id}
        type={type}
        min={type === 'number' ? 0 : undefined}
        step={type === 'number' ? 1 : undefined}
        value={value}
        onChange={(event) => onChange(type === 'number' && event.target.value === '' ? '0' : event.target.value)}
        placeholder={placeholder}
        disabled={disabled}
      />
      <span className="card-target-unit">{unit}</span>
    </div>
  </div>
);

const CardTiming: React.FC<CardTimingProps> = ({
  videoMode,
  fps,
  blocks,
  cardGroups,
  onUpdateCardGroupTiming,
  onApplyTimingToCards,
  onApplyAutoFitToCards,
  onToggleCardGroupLock,
}) => {
  const { selectedProject } = useProject();
  const navigate = useNavigate();
  const [selectedCardGroupId, setSelectedCardGroupId] = useState<string | null>(null);
  const [targetTimeInput, setTargetTimeInput] = useState<string>('00:30:00');
  const [targetFadeInInput, setTargetFadeInInput] = useState<string>('0');
  const [targetFadeOutInput, setTargetFadeOutInput] = useState<string>('0');
  const [targetSlugInput, setTargetSlugInput] = useState<string>('0');
  const [targetInputError, setTargetInputError] = useState<string | null>(null);
  const [isApplyingTarget, setIsApplyingTarget] = useState<boolean>(false);
  const [autoFitResult, setAutoFitResult] = useState<ApplyAutoFitResult | null>(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState<boolean>(false);
  const [applySelectedIds, setApplySelectedIds] = useState<Set<string>>(new Set());
  const [manualApplyPatch, setManualApplyPatch] = useState<Partial<CardTimingValues> | null>(null);
  const [manualApplyField, setManualApplyField] = useState<keyof CardTimingValues | null>(null);
  const [manualApplyValue, setManualApplyValue] = useState<number | null>(null);
  const [manualApplySourceId, setManualApplySourceId] = useState<string | null>(null);
  const [isApplyingManualPatch, setIsApplyingManualPatch] = useState<boolean>(false);
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPersistRef = useRef<{ cardGroupId: string; timing: CardTimingValues } | null>(null);
  const manualDraftRef = useRef<Record<string, CardTimingValues>>({});

  const timeline: CardTimelineItem[] = useMemo(() => buildCardTimeline(blocks, cardGroups), [blocks, cardGroups]);
  const totalFrames: number = useMemo(
    () => timeline.reduce((sum: number, item: CardTimelineItem) => sum + item.durationFrames, 0),
    [timeline]
  );
  const unlockedTimeline: CardTimelineItem[] = useMemo(
    () => timeline.filter((item: CardTimelineItem) => !item.isLocked),
    [timeline]
  );
  const lockedFrames: number = useMemo(
    () => timeline.reduce((sum: number, item: CardTimelineItem) => sum + (item.isLocked ? item.durationFrames : 0), 0),
    [timeline]
  );

  useEffect(() => {
    if (timeline.length === 0) {
      setSelectedCardGroupId(null);
      return;
    }
    if (!selectedCardGroupId || !timeline.some((item: CardTimelineItem) => item.cardGroupId === selectedCardGroupId)) {
      setSelectedCardGroupId(timeline[0].cardGroupId);
    }
  }, [timeline, selectedCardGroupId]);

  useEffect(() => {
    const nextDraft: Record<string, CardTimingValues> = {};
    timeline.forEach((item: CardTimelineItem) => {
      nextDraft[item.cardGroupId] = {
        cardFadeInFrames: item.cardFadeInFrames,
        cardDurationFullyUpFrames: item.cardDurationFullyUpFrames,
        cardFadeOutFrames: item.cardFadeOutFrames,
        cardSlugFrames: item.cardSlugFrames,
      };
    });
    manualDraftRef.current = nextDraft;
  }, [timeline]);

  useEffect(() => {
    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
      }
    };
  }, []);

  const selectedCard: CardTimelineItem | undefined = selectedCardGroupId
    ? timeline.find((item: CardTimelineItem) => item.cardGroupId === selectedCardGroupId)
    : undefined;

  const selectedCardTiming: CardTimingValues | null = selectedCard
    ? {
        cardFadeInFrames: selectedCard.cardFadeInFrames,
        cardDurationFullyUpFrames: selectedCard.cardDurationFullyUpFrames,
        cardFadeOutFrames: selectedCard.cardFadeOutFrames,
        cardSlugFrames: selectedCard.cardSlugFrames,
      }
    : null;
  const timingFields: TimingFieldConfig[] = [
    { field: 'cardFadeInFrames', label: String(t`Fade In`) },
    { field: 'cardDurationFullyUpFrames', label: String(t`Duration fully up`) },
    { field: 'cardFadeOutFrames', label: String(t`Fade Out`) },
    { field: 'cardSlugFrames', label: String(t`Slug`) },
  ];

  const queuePersist = (cardGroupId: string, timing: CardTimingValues): void => {
    pendingPersistRef.current = { cardGroupId, timing };
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
    }
    persistTimerRef.current = setTimeout(() => {
      const pending = pendingPersistRef.current;
      if (!pending) return;
      void onUpdateCardGroupTiming(pending.cardGroupId, pending.timing, { persist: true });
      pendingPersistRef.current = null;
      persistTimerRef.current = null;
    }, 300);
  };

  const handleManualTimingChange = (field: keyof CardTimingValues, value: string): void => {
    if (!selectedCard || !selectedCardTiming || selectedCard.isLocked) return;
    const parsedValue: number = parseFrameInput(value);
    const currentDraft: CardTimingValues = manualDraftRef.current[selectedCard.cardGroupId] ?? selectedCardTiming;
    const fullTiming: CardTimingValues = {
      ...currentDraft,
      [field]: parsedValue,
    };
    manualDraftRef.current[selectedCard.cardGroupId] = fullTiming;
    void onUpdateCardGroupTiming(selectedCard.cardGroupId, { [field]: parsedValue }, { persist: false });
    queuePersist(selectedCard.cardGroupId, fullTiming);
    setManualApplyPatch({ [field]: parsedValue });
    setManualApplyField(field);
    setManualApplyValue(parsedValue);
    setManualApplySourceId(selectedCard.cardGroupId);
    setAutoFitResult(null);
  };

  const getManualApplyOptions = (): CardTimelineItem[] => {
    return unlockedTimeline.filter((item: CardTimelineItem) => item.cardGroupId !== manualApplySourceId);
  };

  const openApplyDialog = (): void => {
    const optionIds: string[] = getManualApplyOptions().map((item: CardTimelineItem) => item.cardGroupId);
    setApplySelectedIds(new Set(optionIds));
    setApplyDialogOpen(true);
  };

  const handleApplySelectionToggle = (cardGroupId: string): void => {
    setApplySelectedIds((previous: Set<string>) => {
      const next = new Set(previous);
      if (next.has(cardGroupId)) {
        next.delete(cardGroupId);
      } else {
        next.add(cardGroupId);
      }
      return next;
    });
  };

  const handleApplySelectionToggleAll = (): void => {
    const optionIds: string[] = getManualApplyOptions().map((item: CardTimelineItem) => item.cardGroupId);
    setApplySelectedIds((previous: Set<string>) => (
      previous.size === optionIds.length ? new Set() : new Set(optionIds)
    ));
  };

  const closeApplyDialog = (): void => {
    setApplyDialogOpen(false);
  };

  const handleToggleCardLock = async (card: CardTimelineItem): Promise<void> => {
    await onToggleCardGroupLock(card.cardGroupId, !card.isLocked);
  };

  const handleApplyManualToMoreCards = async (): Promise<void> => {
    if (!manualApplyPatch || applySelectedIds.size === 0 || isApplyingManualPatch) return;
    try {
      setIsApplyingManualPatch(true);
      await onApplyTimingToCards(Array.from(applySelectedIds), manualApplyPatch);
      setManualApplyPatch(null);
      setManualApplyField(null);
      setManualApplyValue(null);
      setManualApplySourceId(null);
      closeApplyDialog();
    } finally {
      setIsApplyingManualPatch(false);
    }
  };

  const handleApplyTargetTime = async (): Promise<void> => {
    if (isApplyingTarget) return;
    const targetFrames: number | null = parseClockToFrames(targetTimeInput, fps);
    if (targetFrames === null) {
      setTargetInputError(String(t`Invalid target time. Use mm:ss:ff.`));
      return;
    }

    const fadeInFrames: number = parseFrameInput(targetFadeInInput);
    const fadeOutFrames: number = parseFrameInput(targetFadeOutInput);
    const slugFrames: number = parseFrameInput(targetSlugInput);

    try {
      setIsApplyingTarget(true);
      const result: ApplyAutoFitResult = await onApplyAutoFitToCards(
        targetFrames,
        fadeInFrames,
        fadeOutFrames,
        slugFrames,
        unlockedTimeline.map((item: CardTimelineItem) => item.cardGroupId)
      );
      setAutoFitResult(result);
      closeApplyDialog();
      setTargetInputError(null);
    } finally {
      setIsApplyingTarget(false);
    }
  };

  if (!selectedProject) {
    return null;
  }

  return (
    <div className="card-timing-page">
      <NavBar />
      <div className="card-timing-content">
        <button
          onClick={() => navigate('/app')}
          className="fancy-button"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}
        >
          <ArrowBackIcon style={{ fontSize: '18px' }} />
          {t`Back to Editor`}
        </button>

        <div className="card-timing-header">
          <h1>{t`Card Timing`}</h1>
          <div className="card-timing-total">
            {t`Total project time`}: {formatFramesToClock(totalFrames, fps)} ({totalFrames} {t`frames`})
          </div>
        </div>

        {videoMode !== 'cards' ? (
          <Alert severity="info">{t`Card Timing is available only when mode is set to Cards.`}</Alert>
        ) : (
          <>
            {autoFitResult ? (
              <Alert severity={autoFitResult.deltaFrames === 0 ? 'success' : 'info'} className="card-timing-autofit-alert">
                {t`Target applied.`} {t`Delta`}: {autoFitResult.deltaFrames} {t`frames`} ({formatSignedClock(autoFitResult.deltaFrames, fps)})
              </Alert>
            ) : null}

            <div className="card-timing-layout">
              <div className="card-timeline-list">
                {timeline.map((item: CardTimelineItem) => {
                  const fadeInPercent: number = (item.cardFadeInFrames / Math.max(1, item.durationFrames)) * 100;
                  const fullyUpPercent: number = (item.cardDurationFullyUpFrames / Math.max(1, item.durationFrames)) * 100;
                  const fadeOutPercent: number = (item.cardFadeOutFrames / Math.max(1, item.durationFrames)) * 100;
                  const slugPercent: number = Math.max(0, 100 - fadeInPercent - fullyUpPercent - fadeOutPercent);
                  const isSelected: boolean = item.cardGroupId === selectedCardGroupId;

                  return (
                    <div
                      key={item.cardGroupId}
                      className={`card-timeline-row${isSelected ? ' selected' : ''}${item.isLocked ? ' locked' : ''}`}
                      onClick={() => setSelectedCardGroupId(item.cardGroupId)}
                      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedCardGroupId(item.cardGroupId);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="card-timeline-row-head">
                        <span className="card-name">{item.cardDisplayName}</span>
                        <div className="card-timeline-bar-shell">
                          <div className="card-timeline-segment fade-in" style={{ width: `${fadeInPercent}%` }}>
                            <span>{item.cardFadeInFrames}</span>
                          </div>
                          <div className="card-timeline-segment fully-up" style={{ width: `${fullyUpPercent}%` }}>
                            <span>{item.cardDurationFullyUpFrames}</span>
                          </div>
                          <div className="card-timeline-segment fade-out" style={{ width: `${fadeOutPercent}%` }}>
                            <span>{item.cardFadeOutFrames}</span>
                          </div>
                          <div className="card-timeline-segment slug" style={{ width: `${slugPercent}%` }}>
                            <span>{item.cardSlugFrames}</span>
                          </div>
                        </div>
                        <span className="card-range">
                          {formatFramesToClock(item.startFrame, fps)} - {formatFramesToClock(item.endFrame, fps)}
                        </span>
                      </div>
                      <div className="card-timeline-row-foot">
                        <div className="card-timeline-lock-stack">
                          <span>{formatFramesToClock(item.durationFrames, fps)}</span>
                          <span>{item.durationFrames} {t`frames`}</span>
                          <IconButton
                            className={`card-row-lock-toggle${item.isLocked ? ' locked' : ''}`}
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                              event.stopPropagation();
                              void handleToggleCardLock(item);
                            }}
                            title={item.isLocked ? String(t`Unlock block`) : String(t`Lock block`)}
                            aria-label={item.isLocked ? String(t`Unlock block`) : String(t`Lock block`)}
                            size="small"
                          >
                            {item.isLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                          </IconButton>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="card-timing-side">
                <div className="card-timing-panel">
                  <h2>{t`Set Target Time`}</h2>
                  <p>{t`Set project target and auto-balance fully-up duration across unlocked cards.`}</p>
                  <div className="card-field-grid">
                    <RowInput
                      id="target-time-input"
                      label={String(t`Target time`)}
                      value={targetTimeInput}
                      unit="mm:ss:ff"
                      type="text"
                      placeholder="00:00:00"
                      onChange={setTargetTimeInput}
                    />
                    <RowInput
                      id="target-fade-in-input"
                      label={String(t`Fade In`)}
                      value={targetFadeInInput}
                      unit={String(t`frames`)}
                      onChange={setTargetFadeInInput}
                    />
                    <RowInput
                      id="target-fade-out-input"
                      label={String(t`Fade Out`)}
                      value={targetFadeOutInput}
                      unit={String(t`frames`)}
                      onChange={setTargetFadeOutInput}
                    />
                    <RowInput
                      id="target-slug-input"
                      label={String(t`Slug`)}
                      value={targetSlugInput}
                      unit={String(t`frames`)}
                      onChange={setTargetSlugInput}
                    />
                  </div>
                  <div className="card-target-summary">
                    {lockedFrames > 0 ? `${String(t`Locked`)}: ${formatFramesToClock(lockedFrames, fps)}. ` : ''}
                    {unlockedTimeline.length} {t`cards remaining`}
                  </div>
                  {targetInputError ? <Alert severity="error">{targetInputError}</Alert> : null}
                  <Button
                    variant="contained"
                    onClick={handleApplyTargetTime}
                    disabled={unlockedTimeline.length === 0 || isApplyingTarget}
                    startIcon={isApplyingTarget ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {isApplyingTarget ? t`Applying...` : t`Automatically Assign Timing`}
                  </Button>
                </div>
                <div className="card-timing-panel card-settings-panel">
                  <h2>{t`Card Settings`}</h2>
                  {selectedCard ? (
                    <>
                      <div className="card-selected-name">{selectedCard.cardDisplayName}</div>
                      {selectedCard.isLocked ? (
                        <Alert severity="info">{t`Locked cards keep their current duration and are skipped by automatic timing changes.`}</Alert>
                      ) : null}
                      <div className="card-field-grid">
                        {timingFields.map(({ field, label }: TimingFieldConfig) => (
                          <RowInput
                            key={field}
                            id={`card-${field}`}
                            label={label}
                            value={selectedCard[field]}
                            unit={String(t`frames`)}
                            disabled={selectedCard.isLocked}
                            onChange={(value: string) => handleManualTimingChange(field, value)}
                          />
                        ))}
                      </div>
                      <div className="card-full-time">
                        {t`Full time`}: {formatFramesToClock(selectedCard.durationFrames, fps)} ({selectedCard.durationFrames} {t`frames`})
                      </div>
                      {manualApplyPatch && manualApplySourceId === selectedCard.cardGroupId && !selectedCard.isLocked && getManualApplyOptions().length > 0 ? (
                        <Button variant="outlined" onClick={openApplyDialog}>
                          {t`Apply to more cards`}
                        </Button>
                      ) : null}
                    </>
                  ) : (
                    <p>{t`No cards available.`}</p>
                  )}
                </div>

              </div>
            </div>
          </>
        )}
      </div>

      <Dialog open={applyDialogOpen} onClose={closeApplyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t`Apply to more cards`}</DialogTitle>
        <DialogContent className="card-timing-target-dialog">
          {manualApplyField && manualApplyValue !== null ? (
            <p className="card-apply-dialog-lead">
              {`${timingFields.find((config) => config.field === manualApplyField)?.label ?? String(t`Setting`)}: ${manualApplyValue} ${String(t`frames`)}`}
            </p>
          ) : null}
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={applySelectedIds.size === getManualApplyOptions().length && getManualApplyOptions().length > 0}
                  indeterminate={applySelectedIds.size > 0 && applySelectedIds.size < getManualApplyOptions().length}
                  onChange={handleApplySelectionToggleAll}
                  color="primary"
                />
              }
              label={<strong>{applySelectedIds.size === getManualApplyOptions().length ? t`Unselect All` : t`Select All`}</strong>}
            />
            <div className="card-apply-dialog-list">
              {getManualApplyOptions().map((item: CardTimelineItem) => (
                <FormControlLabel
                  key={item.cardGroupId}
                  control={
                    <Checkbox
                      checked={applySelectedIds.has(item.cardGroupId)}
                      onChange={() => handleApplySelectionToggle(item.cardGroupId)}
                      color="primary"
                    />
                  }
                  label={`${item.cardDisplayName} - ${formatFramesToClock(item.durationFrames, fps)}`}
                />
              ))}
            </div>
          </FormGroup>
          {getManualApplyOptions().length === 0 ? (
            <Alert severity="info">{t`No unlocked cards are available for this timing change.`}</Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApplyDialog} disabled={isApplyingTarget || isApplyingManualPatch}>{t`Cancel`}</Button>
          <Button
            onClick={handleApplyManualToMoreCards}
            variant="contained"
            disabled={isApplyingTarget || isApplyingManualPatch || applySelectedIds.size === 0}
            startIcon={isApplyingTarget || isApplyingManualPatch ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isApplyingTarget || isApplyingManualPatch ? t`Applying...` : t`Apply`}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CardTiming;
